//
// DISCLAIMER
//
// Copyright 2024 ArangoDB GmbH, Cologne, Germany
//

import React, { useEffect, useMemo, useState } from "react";

import { Dropdown } from "semantic-ui-react";
import { Field, FieldContent as FC, FieldLabelWide as FL } from "../../../ui/lib";

import { useDeploymentStore } from "../../../util/storage/DeploymentStore";

import apiclients from "../../../api/apiclients";
import IConfigurationViewItemProps from "./IConfigurationViewItemProps";
import { RenderGuard } from "../../../util/RenderGuard";
import { useArangoPlatformFeature } from "../useArangoPlatformFeature";
import { useArangoPlatform } from "../useArangoPlatform";

const BUNDLE_NOT_SET_LABEL = "[not set]";

const platformBundleUpdatePermission = "internal-dashboard.deployment.update-arangodb-platform-bundle" as const;

export const ArangoDBPlatformEnableView = ({ ...args }: IConfigurationViewItemProps) => {
  const { deployment } = useDeploymentStore();

  const { requireComment, requirePermission, organizationId, regionId } = args;
  const { authenticationOnly, idashboardClient } = apiclients;
  const [bundles, setBundles] = useState<{ id: string; name: string }[]>([]);
  const { isArangoPlatformFeature } = useArangoPlatformFeature(organizationId);
  const { isArangoDBPlatformEnabled } = useArangoPlatform(organizationId, regionId);

  useEffect(() => {
    const loadBundles = async () => {
      if (!organizationId || !regionId) {
        setBundles([]);
        return;
      }
      try {
        const res = await authenticationOnly.dataClient.ListArangoPlatformBundles({ organization_id: organizationId, region_id: regionId });
        const items = (res.items || [])
          .slice()
          .sort((a, b) => (a.priority || 0) - (b.priority || 0))
          .map((item) => ({ id: item.id || "", name: item.name || item.id || "" }))
          .filter((item) => !!item.id);
        setBundles(items);
      } catch {
        setBundles([]);
      }
    };
    void loadBundles();
  }, [authenticationOnly.dataClient, organizationId, regionId]);

  const selectedBundleId = deployment.arangodb_platform_bundle || "";
  const selectedBundleName = useMemo(() => {
    if (!selectedBundleId) {
      return "";
    }
    const selectedBundle = bundles.find((bundle) => bundle.id === selectedBundleId);
    if (!selectedBundle) {
      return "";
    }
    return selectedBundle.name;
  }, [bundles, selectedBundleId]);

  const bundleDisplayLabel = !selectedBundleId ? BUNDLE_NOT_SET_LABEL : selectedBundleName || selectedBundleId;

  const onUpdateArangoDBPlatformBundle = async (bundleId: string) => {
    requireComment({
      defaultComment: `Set ArangoDB Platform bundle to ${bundleId}`,
      loadingMessage: `Updating ArangoDB Deployment, please wait...`,
      onCommentConfirmation: async () => {
        await idashboardClient.UpdateDeploymentArangodbPlatformBundle({
          deployment_id: deployment.id,
          arangodb_platform_bundle: bundleId,
        });
      },
    });
  };

  const onUpdateArangoDBPlatformEnabled = async (enable: boolean) => {
    requireComment({
      defaultComment: `${enable ? "Enable" : "Disable"} ArangoDB Platform`,
      loadingMessage: `Updating ArangoDB Deployment, please wait...`,
      onCommentConfirmation: async () => {
        await idashboardClient.UpdateDeploymentArangodbPlatformEnabled({
          deployment_id: deployment.id,
          arangodb_platform_enabled: enable,
        });
      },
    });
  };

  return (
    <Field>
      <FL>ArangoDB Platform</FL>
      <FC>
        {!isArangoPlatformFeature ? (
          <>
            <span>{isArangoDBPlatformEnabled ? "Enabled" : "Disabled"}</span>
            <RenderGuard renderIf={requirePermission("data.deployment.update")}>
              <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
                <Dropdown.Menu>
                  {isArangoDBPlatformEnabled ? (
                    <Dropdown.Item key="disable" text="Disable" onClick={() => onUpdateArangoDBPlatformEnabled(false)} />
                  ) : (
                    <Dropdown.Item key="enable" text="Enable" onClick={() => onUpdateArangoDBPlatformEnabled(true)} />
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </RenderGuard>
          </>
        ) : (
          <>
            <span>{bundleDisplayLabel}</span>
            <RenderGuard renderIf={requirePermission(platformBundleUpdatePermission)}>
              <Dropdown icon="pencil" inline className="icon tiny edit-pencil">
                <Dropdown.Menu>
                  {bundles.map((bundle) => (
                    <Dropdown.Item key={bundle.id} text={bundle.name} onClick={() => onUpdateArangoDBPlatformBundle(bundle.id)} />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </RenderGuard>
          </>
        )}
      </FC>
    </Field>
  );
};
